import { Flex, VStack } from '@chakra-ui/layout'
import type { ReactElement } from 'react'
import React, { useContext, useMemo, useState } from 'react'
import { ACLPermissionEnum, MLViewType } from '../../../constants'
import { MLContext, UserContext } from '../../../providers'
import { isRestrictedUser } from '../../../../utils/roles'
import { PublicLinksInfoContext } from '../../../providers/PublicLinksInfoProvider'
import AccessDeniedView from './AccessDeniedView'
import FilesGrid from './GridView/FilesGrid'
import FoldersGrid from './GridView/FoldersGrid'
import FilesList from './ListView/FilesList'
import FoldersList from './ListView/FoldersList'
import NoFilesOrFoldersView from './NoFilesOrFoldersView'
import NotFoundView from './NotFoundView'
import EmptyFolderView from './EmptyFolderView'
import MLGridLoader from './GridView/MLGridLoader'
import MLListLoader from './ListView/MLListLoader'

const MLView = (): ReactElement => {
  const [
    {
      folderList,
      fileList,
      copyItems,
      moveItems,
      loading,
      loadingFiles,
      loadingFolders,
      view,
      viewableParent,
      folderPermission,
      folderPath,
      loadingViewableParent,
      loadingFolderPermission,
    },
  ] = useContext(MLContext)

  const { isPublicLink } = useContext(PublicLinksInfoContext)
  const { user } = useContext(UserContext)
  const [loadComplete, setLoadComplete] = useState<{
    files: boolean
    folders: boolean
  }>({
    files: false,
    folders: false,
  })

  const setOnLoadComplete = (type: 'folder' | 'file'): void => {
    if (loadComplete.files && loadComplete.folders) {
      return
    }

    setLoadComplete(prev => {
      if (type === 'folder') {
        return {
          ...prev,
          folders: true,
        }
      }

      return {
        ...prev,
        files: true,
      }
    })
  }

  const childComponent = useMemo<React.ReactElement>(() => {
    if (
      loading ||
      loadingFiles ||
      loadingFolders ||
      loadingViewableParent ||
      loadingFolderPermission
    ) {
      return view === MLViewType.GRID ? <MLGridLoader /> : <MLListLoader />
    }

    if (viewableParent === 'NOT_ALLOWED') {
      return <AccessDeniedView />
    }

    if (viewableParent === 'NOT_FOUND') {
      return <NotFoundView />
    }

    if (
      !isPublicLink &&
      isRestrictedUser(user?.roles) &&
      (!(
        folderPermission === ACLPermissionEnum.CONTRIBUTE ||
        folderPermission === ACLPermissionEnum.MANAGE
      ) ||
        folderPath === '/') &&
      folderList.length === 0 &&
      fileList.length === 0 &&
      !loadingFiles &&
      !loadingFolders
    ) {
      return <EmptyFolderView />
    }

    if (
      folderList.length === 0 &&
      fileList.length === 0 &&
      !loadingFiles &&
      !loadingFolders
    ) {
      return <NoFilesOrFoldersView />
    }

    return (
      <VStack alignItems="flex-start" spacing={8}>
        {view === MLViewType.GRID ? (
          <FoldersGrid
            setOnLoadComplete={setOnLoadComplete}
            loadComplete={loadComplete}
          />
        ) : null}
        {view === MLViewType.LIST ? (
          <FoldersList
            setOnLoadComplete={setOnLoadComplete}
            loadComplete={loadComplete}
          />
        ) : null}
        {view === MLViewType.GRID ? (
          <FilesGrid
            setOnLoadComplete={setOnLoadComplete}
            loadComplete={loadComplete}
          />
        ) : null}
        {view === MLViewType.LIST ? (
          <FilesList
            setOnLoadComplete={setOnLoadComplete}
            loadComplete={loadComplete}
          />
        ) : null}
      </VStack>
    )
  }, [
    folderList,
    fileList,
    loading,
    loadingFiles,
    loadingFolders,
    viewableParent,
    view,
    folderPermission,
    folderPath,
    user,
    isPublicLink,
    loadComplete.files,
    loadComplete.folders,
  ])
  return (
    <Flex height="100%" direction="column">
      {childComponent}
    </Flex>
  )
}

export default MLView
